/**
 * Hermes Agent API client — local gateway (OpenAI-compatible).
 */
import { getHermesBridgeConfig } from '../config/hermesConfig';
import { getAgentModule } from '../config/modules';
import { getAgentMode } from '../config/agentModes';
import i18n from '../../i18n';

const hp = (key, opts) => i18n.t(`modules.shared.aiAgent.hermesClient.${key}`, opts);

let lastHealth = { ok: false, checkedAt: 0 };

export async function checkHermesHealth(force = false) {
  const now = Date.now();
  if (!force && now - lastHealth.checkedAt < 15000) {
    return lastHealth;
  }

  const { apiBase, apiKey } = getHermesBridgeConfig();
  if (!apiKey) {
    lastHealth = { ok: false, checkedAt: now, reason: 'no_api_key' };
    return lastHealth;
  }

  try {
    const res = await fetch(`${apiBase}/v1/capabilities`, {
      headers: { Authorization: `Bearer ${apiKey}` },
      signal: AbortSignal.timeout(3000),
    });
    lastHealth = {
      ok: res.ok,
      checkedAt: now,
      status: res.status,
      engine: 'hermes',
    };
  } catch (err) {
    lastHealth = { ok: false, checkedAt: now, reason: err.message, engine: 'hermes' };
  }
  return lastHealth;
}

function buildSystemPrompt({ moduleId, mode, localData }) {
  const mod = getAgentModule(moduleId);
  const modeDef = getAgentMode(mode);
  const moduleLabel = mod?.label || hp('default_module_label');
  const domains = mod?.domains || hp('default_domains');

  return hp('system_prompt', {
    moduleLabel,
    domains,
    modeLabel: modeDef?.label || mode,
    modeDescription: modeDef?.description || '',
    localContext: JSON.stringify(localData || {}, null, 2).slice(0, 12000),
  });
}

function parseDecisionPackage(text) {
  if (!text) return null;
  const blockMatch = text.match(/```(?:json:qawaem-decision|qawaem-decision)?\s*([\s\S]*?)```/i);
  const raw = blockMatch ? blockMatch[1] : null;
  if (!raw) return null;
  try {
    return JSON.parse(raw.trim());
  } catch {
    return null;
  }
}

export async function sendHermesChatRequest({
  message,
  moduleId = 'general',
  mode = 'agent',
  history = [],
  localData = {},
  sessionId,
  sessionKey,
}) {
  const { apiBase, apiKey } = getHermesBridgeConfig();
  if (!apiKey) {
    throw new Error(hp('api_key_missing'));
  }

  const system = buildSystemPrompt({ moduleId, mode, localData });
  const messages = [
    { role: 'system', content: system },
    ...history.slice(-8).map((h) => ({ role: h.role, content: h.content })),
    { role: 'user', content: message },
  ];

  const headers = {
    Authorization: `Bearer ${apiKey}`,
    'Content-Type': 'application/json',
  };
  if (sessionId) headers['X-Hermes-Session-Id'] = sessionId;
  if (sessionKey) headers['X-Hermes-Session-Key'] = sessionKey;

  const res = await fetch(`${apiBase}/v1/chat/completions`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      model: 'hermes-agent',
      messages,
      stream: false,
    }),
  });

  if (!res.ok) {
    const errText = await res.text().catch(() => '');
    throw new Error(hp('api_error', { status: res.status, error: errText.slice(0, 300) }));
  }

  const data = await res.json();
  const response =
    data.choices?.[0]?.message?.content ||
    data.output_text ||
    data.response ||
    '';

  const decisionPackage = parseDecisionPackage(response);

  return {
    response,
    engine: 'hermes',
    decisionPackage,
    raw: data,
  };
}

export async function fetchPendingApprovals(sessionId) {
  const base = process.env.REACT_APP_API_URL || 'http://127.0.0.1:5001/api';
  const q = sessionId ? `?sessionId=${encodeURIComponent(sessionId)}` : '';
  const res = await fetch(`${base}/agent/approvals/pending${q}`);
  if (!res.ok) return { items: [] };
  return res.json();
}

export async function approveHermesDraft(approvalId) {
  const base = process.env.REACT_APP_API_URL || 'http://127.0.0.1:5001/api';
  const res = await fetch(`${base}/agent/approvals/${approvalId}/approve`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: '{}',
  });
  return res.json();
}

export async function rejectHermesDraft(approvalId) {
  const base = process.env.REACT_APP_API_URL || 'http://127.0.0.1:5001/api';
  const res = await fetch(`${base}/agent/approvals/${approvalId}/reject`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: '{}',
  });
  return res.json();
}

export { checkHermesHealth as getHermesEngineStatus };
